// The configurator (/configurator, Sales): a boat's order built section by section (order, buyer, delivery,
// vessel, standard equipment, options, custom requests) with its price worked out as it goes. A new order is
// saved as a draft (then saved by itself as it changes) and confirmed when it's complete; a confirmed one is
// changed by saving a new revision. Confirming or revising files the order for production: the worker writes
// the boat's Order Sheet row from the catalogue items' ties (Configuration Setup) and saves its order file, and
// this page then makes the order and BOM workbooks and keeps them in Info / Files (features/orderFiling.ts).

import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  awaitsAcceptance, canReviseOrder, customerDisplayName, orderPricing, orderStage, ORDER_STAGES, DEALER_DISCOUNT_PCT, VAT_PCT,
  type ConfiguredOrderFiling, type Customer, type OrderDraftInput, type OrderSnapshot, type OrderSummary, type Permission,
} from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { usePhotos, type PhotoMeta } from '../features/hooks.js';
import { exportSalesAgreement } from '../features/exportSalesAgreement.js';
import { storeOrderFiles, type StoredOrderFiles } from '../features/orderFiling.js';
import { CustomerPicker } from '../components/CustomerPicker.js';
import { ActionBar, EmptyState, ErrorState, FilterToggle, InlineError, LoadingState, LogoSpin, Modal, PageHeader, Pill, SearchInput } from '../components/ui.js';
import { IconAlertTriangle, IconCheck, IconChevronLeft, IconChevronRight, IconClose, IconFile, IconLock, IconPlus, IconTrash, IconUnlock } from '../components/icons.js';
import { useBackToClose } from '../lib/media.js';
import { useDateFormatter } from '../lib/format.js';
import { fold, hasText } from '../lib/search.js';
import {
  useCatalogue,
  useConfirmOrder,
  useCreateDraft,
  useEditOrder,
  useFileOrder,
  useOrder,
  useUpdateDraft,
  type CatalogueItem,
} from '../features/orders.js';

const money = (n: number) => `€${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface Line {
  selected?: boolean;
  removedFromStandard?: boolean;
  quantity?: number;
  dealerPrice?: number | null;
  customerPrice?: number | null;
  color?: string | null;
  drawingReference?: string | null;
  customerComment?: string | null;
  productionComment?: string | null;
  lowerUnit?: string | null;
  esEh?: string | null;
  propellers?: string | null;
  premier?: string | null;
  tieBar?: string | null;
  preRigDelivery?: string | null;
  enginesDelivery?: string | null;
}
interface CustomItem {
  title: string;
  description?: string;
  quantity?: number;
}
interface FormState {
  proformaNumber: string;
  orderDate: string;
  salesRep: string;
  dealer: string;
  internalComments: string;
  buyer: { name: string; registeredAddress: string; vatTaxCode: string; profession: string; email: string; telephone: string; customerId: number | null };
  delivery: { method: string; destination: string; estimatedDeliveryPeriod: string; requestedDeliveryDate: string; comments: string };
  vessel: { modelYear: string; flag: string; vesselName: string; sapNumber: string; hullNumber: string };
  lines: Record<number, Line>;
  customItems: CustomItem[];
}

const emptyForm = (): FormState => ({
  proformaNumber: '', orderDate: '', salesRep: '', dealer: '', internalComments: '',
  buyer: { name: '', registeredAddress: '', vatTaxCode: '', profession: '', email: '', telephone: '', customerId: null },
  delivery: { method: '', destination: '', estimatedDeliveryPeriod: '', requestedDeliveryDate: '', comments: '' },
  vessel: { modelYear: '', flag: '', vesselName: '', sapNumber: '', hullNumber: '' },
  lines: {}, customItems: [],
});

/** map a selected customer master record onto the order's buyer fields (auto-populate). */
function customerToBuyer(c: Customer): FormState['buyer'] {
  return {
    name: customerDisplayName(c),
    registeredAddress: c.registeredAddress || [c.street, c.postalCode, c.city, c.country].filter(Boolean).join(', '),
    vatTaxCode: c.vatNumber || c.taxCode,
    profession: c.profession,
    email: c.email,
    telephone: c.telephone || c.mobile,
    customerId: c.id,
  };
}

function toInput(f: FormState, modelId: number): OrderDraftInput {
  const equipment = Object.entries(f.lines)
    .filter(([, l]) => l.selected || l.removedFromStandard || l.color || l.drawingReference || l.customerComment || l.productionComment)
    .map(([id, l]) => ({
      equipmentId: Number(id),
      selected: !!l.selected,
      removedFromStandard: !!l.removedFromStandard,
      quantity: l.quantity ?? 1,
      dealerPrice: l.dealerPrice ?? null,
      customerPrice: l.customerPrice ?? null,
      color: l.color ?? null,
      drawingReference: l.drawingReference ?? null,
      customerComment: l.customerComment ?? null,
      productionComment: l.productionComment ?? null,
      lowerUnit: l.lowerUnit ?? null, esEh: l.esEh ?? null, propellers: l.propellers ?? null,
      premier: l.premier ?? null, tieBar: l.tieBar ?? null, preRigDelivery: l.preRigDelivery ?? null,
      enginesDelivery: l.enginesDelivery ?? null,
    }));
  return {
    proformaNumber: f.proformaNumber || null,
    orderDate: f.orderDate || null,
    salesRep: f.salesRep || null,
    dealer: f.dealer || null,
    internalComments: f.internalComments || null,
    buyer: f.buyer,
    delivery: f.delivery,
    vessel: { ...f.vessel, modelId, modelYear: f.vessel.modelYear ? Number(f.vessel.modelYear) : null },
    equipment,
    customItems: f.customItems.filter((c) => c.title.trim()),
    productionRequirements: [],
  } as unknown as OrderDraftInput;
}

function snapshotToForm(sn: OrderSnapshot): FormState {
  const f = emptyForm();
  f.proformaNumber = sn.proformaNumber ?? '';
  f.orderDate = sn.orderDate ?? '';
  f.salesRep = sn.salesRep ?? '';
  f.dealer = sn.dealer ?? '';
  f.internalComments = sn.internalComments ?? '';
  f.buyer = {
    name: sn.buyer?.name ?? '', registeredAddress: sn.buyer?.registeredAddress ?? '', vatTaxCode: sn.buyer?.vatTaxCode ?? '',
    profession: sn.buyer?.profession ?? '', email: sn.buyer?.email ?? '', telephone: sn.buyer?.telephone ?? '',
    customerId: sn.buyer?.customerId ?? null,
  };
  f.delivery = {
    method: sn.delivery?.method ?? '', destination: sn.delivery?.destination ?? '', estimatedDeliveryPeriod: sn.delivery?.estimatedDeliveryPeriod ?? '',
    requestedDeliveryDate: sn.delivery?.requestedDeliveryDate ?? '', comments: sn.delivery?.comments ?? '',
  };
  f.vessel = {
    modelYear: sn.vessel?.modelYear ? String(sn.vessel.modelYear) : '', flag: sn.vessel?.flag ?? '',
    vesselName: sn.vessel?.vesselName ?? '', sapNumber: sn.vessel?.sapNumber ?? '', hullNumber: sn.vessel?.hullNumber ?? '',
  };
  for (const e of sn.equipment) {
    f.lines[e.equipmentId] = {
      selected: e.selected, removedFromStandard: e.removedFromStandard, quantity: e.quantity,
      customerPrice: e.customerPrice, dealerPrice: e.dealerPrice, color: e.color, drawingReference: e.drawingReference,
      customerComment: e.customerComment, productionComment: e.productionComment, lowerUnit: e.lowerUnit, esEh: e.esEh,
      propellers: e.propellers, premier: e.premier, tieBar: e.tieBar, preRigDelivery: e.preRigDelivery, enginesDelivery: e.enginesDelivery,
    };
  }
  f.customItems = (sn.customItems ?? []).map((c) => ({ title: c.title, description: c.description ?? undefined, quantity: c.quantity }));
  return f;
}

/** what confirming or revising did, step by step, for the receipt */
interface Receipt {
  kind: 'confirmed' | 'revised' | 'refiled';
  revision: number;
  /** the boat's Order Sheet row and order file record, or why they weren't written */
  filing?: ConfiguredOrderFiling;
  filingError?: string;
  /** the order and BOM workbooks in Info / Files */
  files?: StoredOrderFiles;
  filesError?: string;
  busy: boolean;
}

const SECTIONS = [
  { key: 'order', label: 'Order information' },
  { key: 'buyer', label: 'Buyer details' },
  { key: 'delivery', label: 'Delivery details' },
  { key: 'vessel', label: 'Vessel details' },
  { key: 'standard', label: 'Standard equipment' },
  { key: 'optional', label: 'Optional equipment' },
  { key: 'custom', label: 'Custom requests' },
] as const;
type SectionKey = (typeof SECTIONS)[number]['key'];

export function Configurator() {
  const { can, me } = useAuth();
  const df = useDateFormatter();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('id') ? Number(params.get('id')) : null;
  const existing = useOrder(editId);
  // an order opens in its own model's catalogue; a new one in the default model's
  const orderModelId = existing.data?.snapshot.vessel?.modelId ?? undefined;
  const catalogue = useCatalogue(editId ? orderModelId : undefined);
  const cataloguePhotos = usePhotos('catalogue');
  const photosByRef = useMemo(() => {
    const m = new Map<string, PhotoMeta[]>();
    for (const p of cataloguePhotos.data?.items ?? []) { const a = m.get(p.refKey) ?? []; a.push(p); m.set(p.refKey, a); }
    return m;
  }, [cataloguePhotos.data]);
  const createDraft = useCreateDraft();
  const updateDraft = useUpdateDraft();
  const confirmOrder = useConfirmOrder();
  const editOrderMut = useEditOrder();
  const fileOrder = useFileOrder();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [status, setStatus] = useState('draft');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  // enlarged photo overlay: a photo URL, or 'gallery' for the model gallery's current slide (it can step)
  const [lightbox, setLightbox] = useState<string | null>(null);
  const lbOpener = useRef<HTMLElement | null>(null); // the thumbnail that opened it, for focus on close
  const openLightbox = (src: string, from: HTMLElement) => { lbOpener.current = from; setLightbox(src); };
  const [galleryIdx, setGalleryIdx] = useState(0); // active slide in the model gallery
  const stageRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);         // section nav (a sticky sideways strip ≤900px)
  const mainRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLElement>(null);
  const committed = ['confirmed', 'changed', 'locked', 'completed'].includes(status);
  // a confirmed order is locked for Sales: Assembly (and administrators) change it any time; Sales only while
  // Assembly has it open for them, never in production (shared orderStage.ts, as the worker enforces it)
  const held = existing.data?.order;
  const revise = committed && held ? canReviseOrder(held, (p) => can(p as Permission)) : { ok: true, why: '' };
  const readOnly = committed && !revise.ok;
  const manager = can('orderlock:manage') || can('admin:users');

  // load an existing order into the form (once).
  const loadedRef = useRef(false);
  useEffect(() => {
    if (existing.data && !loadedRef.current) {
      loadedRef.current = true;
      setForm(snapshotToForm(existing.data.snapshot));
      setOrderId(existing.data.order.id);
      setOrderNumber(existing.data.order.orderNumber);
      setStatus(existing.data.order.status);
    }
  }, [existing.data]);
  const [active, setActive] = useState<SectionKey>('order');
  const [search, setSearch] = useState('');
  const [chosenOnly, setChosenOnly] = useState(false);
  const [offOnly, setOffOnly] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [printErr, setPrintErr] = useState<Error | null>(null); // the agreement's print pop-up was blocked

  const model = catalogue.data?.model ?? null;
  const canWrite = can('order:create');

  const patch = (fn: (f: FormState) => void) => setForm((prev) => { const next = structuredClone(prev); fn(next); return next; });
  const touch = () => setDirty(true);
  const setLine = (id: number, l: Partial<Line>) => { patch((f) => { f.lines[id] = { ...f.lines[id], ...l }; }); touch(); };

  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const formRef = useRef(form);
  formRef.current = form;
  const busy = createDraft.isPending || updateDraft.isPending;

  /** save the draft (the first save creates the order); `then` runs once it's saved */
  const save = (then?: (id: number) => void) => {
    if (!model || busy) return;
    const payload = toInput(formRef.current, model);
    if (orderId) {
      updateDraft.mutate({ id: orderId, draft: payload }, { onSuccess: () => { setSavedAt(Date.now()); setDirty(false); then?.(orderId); } });
    } else {
      createDraft.mutate(payload, {
        onSuccess: (r) => { setOrderId(r.id); setOrderNumber(r.orderNumber); setSavedAt(Date.now()); setDirty(false); then?.(r.id); },
      });
    }
  };

  useEffect(() => {
    if (!dirty || !orderId || committed) return; // committed orders are revised explicitly, not autosaved
    const t = setTimeout(save, 1600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dirty, orderId, committed]);

  // the order's workbooks, once the worker has filed it: made here and kept in Info / Files
  const stamp = (f: ConfiguredOrderFiling) => `Revision ${f.revision} · ${df.dateTime(new Date().toISOString())} · ${me?.user.displayName || me?.user.email || ''}`;
  const storeFiles = (filing: ConfiguredOrderFiling) => {
    setReceipt((r) => (r ? { ...r, filing, filingError: undefined, filesError: undefined, busy: true } : r));
    storeOrderFiles(filing, stamp(filing))
      .then((files) => setReceipt((r) => (r ? { ...r, files, busy: false } : r)))
      .catch((e: unknown) => setReceipt((r) => (r ? { ...r, filesError: e instanceof Error ? e.message : 'The workbooks couldn’t be put in Info / Files.', busy: false } : r)));
  };
  const afterCommit = (kind: Receipt['kind'], res: { revisionNumber: number; orderFile?: ConfiguredOrderFiling; orderFileError?: string }) => {
    setReceipt({ kind, revision: res.revisionNumber, filing: res.orderFile, filingError: res.orderFileError, busy: Boolean(res.orderFile) });
    if (res.orderFile) storeFiles(res.orderFile);
  };
  /** file the order again (filing failed, or the catalogue's ties changed since) */
  const refile = () => {
    if (!orderId) return;
    setReceipt((r) => ({ ...(r ?? { kind: 'refiled', revision: 0 }), busy: true, filingError: undefined, filesError: undefined }));
    fileOrder.mutate(orderId, {
      onSuccess: (filing) => storeFiles(filing),
      onError: (e) => setReceipt((r) => (r ? { ...r, busy: false, filingError: e instanceof Error ? e.message : 'The order couldn’t be filed.' } : r)),
    });
  };

  const saveRevision = () => {
    if (!orderId || !model) return;
    editOrderMut.mutate({ id: orderId, draft: toInput(formRef.current, model) }, { onSuccess: (res) => { setDirty(false); afterCommit('revised', res); } });
  };

  const pricing = useMemo(() => {
    // map each item to its category's discount-eligibility (bespoke & transport = not eligible)
    type CatItem = NonNullable<typeof catalogue.data>['categories'][number]['items'][number];
    const byId = new Map<number, { it: CatItem; eligible: boolean }>();
    for (const c of catalogue.data?.categories ?? []) {
      for (const it of c.items) byId.set(it.id, { it, eligible: c.discountEligible !== false });
    }
    let eligible = 0;
    let nonEligible = 0;
    for (const [id, l] of Object.entries(form.lines)) {
      if (!l.selected) continue;
      const entry = byId.get(Number(id));
      if (entry) {
        if (!entry.it.is_standard) {
          const line = (l.customerPrice ?? entry.it.customer_price ?? 0) * (l.quantity ?? 1);
          if (entry.eligible) eligible += line;
          else nonEligible += line;
        }
      } else if (l.customerPrice != null) {
        // discontinued item still on this order (not in the active catalogue) — keep its frozen price (eligible).
        eligible += l.customerPrice * (l.quantity ?? 1);
      }
    }
    const p = orderPricing(eligible, nonEligible);
    return { optional: p.subtotalOptional, discount: p.discount, net: p.net, vat: p.vat, total: p.total };
  }, [form.lines, catalogue.data]);

  const selectedOptional = Object.values(form.lines).filter((l) => l.selected).length;
  const removedStandard = Object.values(form.lines).filter((l) => l.removedFromStandard).length;
  const customCount = form.customItems.filter((c) => c.title.trim()).length;

  // model gallery: the separated white-vessel photos (`gallery:<code>`), falling back to the single model hero
  // (`model:<code>`) until the gallery is seeded, sorted by filename (1..n). Must stay above the early returns
  // below (rules of hooks).
  const galleryPhotos = useMemo(() => {
    const code = catalogue.data?.models.find((m) => m.id === model)?.code;
    if (!code) return [] as PhotoMeta[];
    const g = photosByRef.get(`gallery:${code}`) ?? [];
    const list = g.length ? g : (photosByRef.get(`model:${code}`) ?? []);
    return [...list].sort((a, b) => a.filename.localeCompare(b.filename, undefined, { numeric: true }));
  }, [catalogue.data, model, photosByRef]);
  const gIdx = galleryPhotos.length ? galleryIdx % galleryPhotos.length : 0;
  const galleryMain = galleryPhotos[gIdx];

  // the photo lightbox closes on Escape (it can be opened from the keyboard) and, on touch, on the back
  // gesture; opened from the model gallery, the arrow keys step through its photos
  const galleryCount = galleryPhotos.length;
  const stepGallery = (d: number) => setGalleryIdx((i) => (((i % galleryCount) + d) % galleryCount + galleryCount) % galleryCount);
  useBackToClose(!!lightbox, () => setLightbox(null));
  // focus goes to the close button on open (autoFocus) and back to the opener on close, as Modal does; the
  // gallery's stage photo is re-keyed per slide, so after stepping, the current one takes it instead
  const lbOpen = !!lightbox;
  useEffect(() => {
    if (!lbOpen) return;
    const opener = lbOpener.current;
    return () => {
      const target = opener && !opener.isConnected && opener.classList.contains('cfg-hero-img')
        ? stageRef.current?.querySelector<HTMLElement>('.cfg-hero-img')
        : opener;
      if (target?.isConnected) target.focus();
    };
  }, [lbOpen]);
  useEffect(() => {
    if (!lightbox) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (lightbox === 'gallery' && galleryCount > 1 && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) stepGallery(e.key === 'ArrowLeft' ? -1 : 1);
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, galleryCount]);

  // early returns keep the page title so the header doesn't vanish / jump while loading
  const shell = (body: ReactNode) => <><PageHeader title="Configurator" />{body}</>;
  if (catalogue.isLoading || (editId && existing.isLoading)) return shell(<LoadingState label="Loading the configurator…" />);
  if (catalogue.error) return shell(<ErrorState error={catalogue.error} onRetry={() => void catalogue.refetch()} />);
  // a failed initial load must not fall through to a blank "New order" form (Save would create a second order).
  // !existing.data: a failed background refetch after an autosave keeps the editor.
  if (editId && existing.error && !existing.data) return shell(<ErrorState error={existing.error} onRetry={() => void existing.refetch()} />);
  // a confirmed order always opens (read-only when it can't be changed); building one needs order:create
  if (!committed && !canWrite) return shell(<div className="state error"><div className="big">Access denied</div><div className="muted">You don't have permission to build orders.</div></div>);

  const cats = catalogue.data?.categories ?? [];
  const stdCats = cats.filter((c) => c.kind === 'standard');
  const optCats = cats.filter((c) => c.kind !== 'standard');
  const q = fold(search.trim());
  const matches = (it: CatalogueItem) => hasText(q, it.name);
  const modelMeta = catalogue.data?.models.find((m) => m.id === model);
  const modelName = modelMeta?.name ?? '';

  const exportAgreement = () => {
    if (!catalogue.data) return;
    const opened = exportSalesAgreement({
      orderNumber,
      proformaNumber: form.proformaNumber,
      orderDate: form.orderDate,
      buyer: form.buyer,
      delivery: { method: form.delivery.method, destination: form.delivery.destination, estimatedDeliveryPeriod: form.delivery.estimatedDeliveryPeriod },
      vessel: { modelName, modelYear: form.vessel.modelYear, flag: form.vessel.flag, vesselName: form.vessel.vesselName, sapNumber: form.vessel.sapNumber, modelCode: modelMeta?.code ?? '' },
      categories: catalogue.data.categories,
      lines: form.lines,
      pricing,
    });
    setPrintErr(opened ? null : new Error('The print window was blocked. Allow pop-ups for this site and try again.'));
  };

  // each section's state in the list: filled in, still needed, or how many it holds
  const filled = (...v: string[]) => v.some((x) => x.trim());
  const sectionState: Record<SectionKey, { tone?: 'ok' | 'warn' | 'info'; text?: string; title: string } | null> = {
    order: filled(form.orderDate, form.proformaNumber, form.salesRep, form.dealer) ? { tone: 'ok', title: 'Filled in' } : null,
    buyer: form.buyer.name.trim() ? { tone: 'ok', title: 'Filled in' } : { tone: 'warn', text: 'Needed', title: 'The buyer’s name is needed to confirm' },
    delivery: filled(form.delivery.method, form.delivery.destination, form.delivery.estimatedDeliveryPeriod, form.delivery.comments) ? { tone: 'ok', title: 'Filled in' } : null,
    vessel: filled(form.vessel.vesselName, form.vessel.sapNumber, form.vessel.hullNumber, form.vessel.flag) ? { tone: 'ok', title: 'Filled in' } : null,
    standard: removedStandard ? { text: `${removedStandard} off`, title: `${removedStandard} standard item${removedStandard === 1 ? '' : 's'} taken off` } : null,
    optional: selectedOptional ? { tone: 'info', text: String(selectedOptional), title: `${selectedOptional} option${selectedOptional === 1 ? '' : 's'} chosen` } : null,
    custom: customCount ? { text: String(customCount), title: `${customCount} custom request${customCount === 1 ? '' : 's'}` } : null,
  };
  const at = SECTIONS.findIndex((s) => s.key === active);
  const prev = SECTIONS[at - 1];
  const next = SECTIONS[at + 1];

  // ≤900px the section nav is a sticky sideways strip: the tapped tab scrolls into view, and when the page
  // is scrolled past the top of the sections (deep in Optional equipment) it comes back to the new heading
  const showSection = (key: SectionKey, tab?: HTMLElement | null) => {
    setActive(key);
    setSearch('');
    const nav = navRef.current;
    const main = mainRef.current;
    if (!nav || !main) return;
    const content = nav.closest<HTMLElement>('.content');
    if (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 900px)').matches) {
      const b = (tab ?? nav.querySelector<HTMLElement>(`[data-sec="${key}"]`))?.getBoundingClientRect();
      const n = nav.getBoundingClientRect();
      if (b) nav.scrollTo({ left: nav.scrollLeft + (b.left - n.left) - (n.width - b.width) / 2 });
      const over = main.getBoundingClientRect().top - (n.bottom + 12);
      if (content && over < 0) content.scrollTo({ top: content.scrollTop + over });
    } else if (content) {
      // stepping on from the foot of a long section lands on the next one's heading
      const over = main.getBoundingClientRect().top - content.getBoundingClientRect().top - 12;
      if (over < 0) content.scrollTo({ top: content.scrollTop + over });
    }
  };
  // the total bar's figure jumps to the full summary, clear of the sticky section nav above it
  const scrollToSummary = () => {
    const sum = summaryRef.current;
    const content = sum?.closest<HTMLElement>('.content');
    if (!sum || !content) return;
    const navBottom = navRef.current?.getBoundingClientRect().bottom ?? content.getBoundingClientRect().top;
    content.scrollTo({ top: content.scrollTop + sum.getBoundingClientRect().top - navBottom - 12 });
  };

  const lightboxSrc = lightbox === 'gallery' ? (galleryMain ? `/api/photos/file?id=${galleryMain.id}` : null) : lightbox;
  const own = (fn: () => void) => (e: MouseEvent) => { e.stopPropagation(); fn(); };

  const confirmMissing: string[] = [];
  if (!form.buyer.name.trim()) confirmMissing.push('the buyer’s name');

  /** open the confirmation: a new order is saved as a draft first */
  const openConfirm = () => { if (orderId) setShowConfirm(true); else save(() => setShowConfirm(true)); };
  const doConfirm = () => {
    if (!orderId) return;
    // already confirmed → this is a revision, not a first confirmation.
    if (committed) { setShowConfirm(false); saveRevision(); return; }
    confirmOrder.mutate(
      { id: orderId },
      {
        onSuccess: (res) => { setShowConfirm(false); setDirty(false); setStatus('confirmed'); afterCommit('confirmed', res); },
        onError: (err) => {
          // confirmed elsewhere / stale view — switch to the revision flow so changes can still be saved.
          if (err instanceof Error && /already confirmed/i.test(err.message)) { setStatus('confirmed'); setShowConfirm(false); }
        },
      },
    );
  };

  const stage = committed && held ? orderStage(held) : null;
  const statusPill = stage ? <Pill kind={ORDER_STAGES[stage].tone || undefined}>{ORDER_STAGES[stage].sales}</Pill> : committed ? <Pill kind="ok">Confirmed</Pill> : orderId ? <Pill>Draft</Pill> : null;
  // dirty wins over savedAt: after a failed save the last "saved" time would be a lie
  const saveState = readOnly ? 'Locked' : committed ? 'Changes save as a new revision' : busy ? 'Saving…' : dirty ? (orderId ? 'Unsaved changes' : 'Not saved yet') : savedAt ? `Saved ${df.time(savedAt)}` : orderId ? 'Saved' : 'Not saved yet';
  const boatName = form.vessel.vesselName.trim() || form.buyer.name.trim();
  const primaryLabel = committed ? (editOrderMut.isPending ? 'Saving…' : 'Save as new revision') : 'Confirm order';
  const primaryAct = committed ? saveRevision : openConfirm;
  const primaryBusy = committed ? editOrderMut.isPending : busy;

  return (
    <>
      <PageHeader
        title="Configurator"
        meta={
          <span className="cfg-meta">
            <b>{modelName || 'No model'}</b>
            {boatName && <span>{boatName}</span>}
            {orderNumber ? <span className="cfg-meta-no">{orderNumber}</span> : <span>New order</span>}
            {statusPill}
            <span className={dirty && !committed ? 'cfg-meta-dirty' : undefined}>{saveState}</span>
          </span>
        }
      >
        {/* .cfg-head-act: phones drop it, the summary and the sticky .cfg-bar carry the actions */}
        {!committed && <button className="btn btn-sm cfg-head-act" disabled={busy || !model} onClick={() => save()}>Save draft</button>}
      </PageHeader>
      {(createDraft.error ?? updateDraft.error) && (
        <InlineError error={createDraft.error ?? updateDraft.error} onDismiss={() => { createDraft.reset(); updateDraft.reset(); }} />
      )}
      {committed && held && <LockLine order={held} readOnly={readOnly} why={revise.why} manager={manager} />}

      {galleryMain && (
        <section className="cfg-hero" aria-label={`${modelName} photos`}>
          <div className="cfg-hero-stage" ref={stageRef}>
            <img
              key={galleryMain.id}
              className="cfg-hero-img"
              src={`/api/photos/file?id=${galleryMain.id}`}
              alt={`${modelName}, photo ${gIdx + 1} of ${galleryCount}`}
              title="Enlarge"
              role="button"
              tabIndex={0}
              onClick={(e) => openLightbox('gallery', e.currentTarget)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLightbox('gallery', e.currentTarget); } }}
            />
            <span className="cfg-hero-name">{modelName}</span>
            {galleryCount > 1 && (
              <>
                <button type="button" className="cfg-hero-arrow prev" aria-label="Previous photo" onClick={() => stepGallery(-1)}><IconChevronLeft size={20} /></button>
                <button type="button" className="cfg-hero-arrow next" aria-label="Next photo" onClick={() => stepGallery(1)}><IconChevronRight size={20} /></button>
                <span className="cfg-hero-count" aria-hidden="true">{gIdx + 1} / {galleryCount}</span>
              </>
            )}
          </div>
          {galleryCount > 1 && (
            <div className="cfg-hero-thumbs">
              {galleryPhotos.map((p, i) => (
                <button key={p.id} type="button" className="cfg-hero-thumb" aria-label={`Show photo ${i + 1} of ${galleryCount}`} aria-pressed={i === gIdx} onClick={() => setGalleryIdx(i)}>
                  <img src={`/api/photos/file?id=${p.id}`} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      <div className="cfg-layout">
        <nav className="cfg-nav" ref={navRef} aria-label="Order sections">
          {SECTIONS.map((s, n) => {
            const st = sectionState[s.key];
            return (
              <button key={s.key} type="button" data-sec={s.key} className={`cfg-nav-item${active === s.key ? ' active' : ''}`} aria-current={active === s.key ? 'step' : undefined} title={st?.title} onClick={(e) => showSection(s.key, e.currentTarget)}>
                <span className="cfg-nav-n" aria-hidden="true">{n + 1}</span>
                <span className="cfg-nav-l">{s.label}</span>
                {st && (st.tone === 'ok' && !st.text
                  ? <span className="cfg-nav-st ok"><IconCheck size={14} /><span className="sr-only">{st.title}</span></span>
                  : <span className={`cfg-nav-st${st.tone ? ` ${st.tone}` : ''}`}>{st.text}<span className="sr-only"> · {st.title}</span></span>)}
              </button>
            );
          })}
        </nav>

        <div className="cfg-main" ref={mainRef}>
          <section className="cfg-sec" aria-labelledby="cfg-sec-t">
            <header className="cfg-sec-hd">
              <h2 id="cfg-sec-t" className="cfg-sec-title">{SECTIONS[at]!.label}</h2>
              {active === 'buyer' && <p className="cfg-sec-hint">Pick the customer to fill these in. The order keeps its own copy of the buyer’s details.</p>}
              {active === 'standard' && <p className="cfg-sec-hint">Everything here comes with the boat. Untick an item to take it off this build.</p>}
              {active === 'optional' && <p className="cfg-sec-hint">Tick what the customer chooses, then set its quantity, price, comment and drawing.</p>}
              {active === 'custom' && <p className="cfg-sec-hint">Requests the catalogue doesn’t have. Each goes on the order file as an additional request.</p>}
            </header>
            {/* a confirmed order that can't be changed reads as it is: every control in the section is off */}
            <fieldset className="cfg-fs" disabled={readOnly}>

            {active === 'order' && (
              <>
                <Grid>
                  <Field label="Order date"><input type="date" className="field" value={form.orderDate} onChange={(e) => { patch((f) => (f.orderDate = e.target.value)); touch(); }} /></Field>
                  <Field label="Proforma number"><input className="field" value={form.proformaNumber} onChange={(e) => { patch((f) => (f.proformaNumber = e.target.value)); touch(); }} /></Field>
                  <Field label="Sales representative"><input className="field" value={form.salesRep} onChange={(e) => { patch((f) => (f.salesRep = e.target.value)); touch(); }} /></Field>
                  <Field label="Dealer"><input className="field" value={form.dealer} onChange={(e) => { patch((f) => (f.dealer = e.target.value)); touch(); }} /></Field>
                </Grid>
                <Field label="Internal comments" note="Not on the customer’s agreement"><textarea className="field" rows={3} value={form.internalComments} onChange={(e) => { patch((f) => (f.internalComments = e.target.value)); touch(); }} /></Field>
              </>
            )}

            {active === 'buyer' && (
              <>
                <div className="cfg-picker">
                  <div className="field-label">Customer</div>
                  <CustomerPicker
                    linkedId={form.buyer.customerId}
                    linkedName={form.buyer.name}
                    newDefaults={{
                      lastName: form.buyer.name,
                      registeredAddress: form.buyer.registeredAddress,
                      vatNumber: form.buyer.vatTaxCode,
                      profession: form.buyer.profession,
                      email: form.buyer.email,
                      telephone: form.buyer.telephone,
                    }}
                    onSelect={(c) => { patch((f) => { Object.assign(f.buyer, customerToBuyer(c)); }); touch(); }}
                    onClear={() => { patch((f) => { f.buyer.customerId = null; }); touch(); }}
                  />
                </div>
                <Grid>
                  {([['name', 'Name and surname'], ['registeredAddress', 'Registered address'], ['vatTaxCode', 'VAT and tax code'], ['profession', 'Profession'], ['email', 'Email'], ['telephone', 'Telephone']] as const).map(([k, label]) => (
                    <Field key={k} label={label} note={k === 'name' ? 'Needed to confirm' : undefined} warn={k === 'name' && !form.buyer.name.trim()}>
                      <input className="field" value={form.buyer[k]} onChange={(e) => { patch((f) => (f.buyer[k] = e.target.value)); touch(); }}
                        {...(k === 'email' ? { type: 'email', autoComplete: 'email' } : k === 'telephone' ? { type: 'tel', autoComplete: 'tel' } : {})} />
                    </Field>
                  ))}
                </Grid>
              </>
            )}

            {active === 'delivery' && (
              <>
                <Grid>
                  <Field label="Delivery method"><input className="field" value={form.delivery.method} onChange={(e) => { patch((f) => (f.delivery.method = e.target.value)); touch(); }} /></Field>
                  <Field label="Destination of transport"><input className="field" value={form.delivery.destination} onChange={(e) => { patch((f) => (f.delivery.destination = e.target.value)); touch(); }} /></Field>
                  <Field label="Estimated delivery period"><input className="field" value={form.delivery.estimatedDeliveryPeriod} onChange={(e) => { patch((f) => (f.delivery.estimatedDeliveryPeriod = e.target.value)); touch(); }} /></Field>
                </Grid>
                <Field label="Delivery comments"><textarea className="field" rows={2} value={form.delivery.comments} onChange={(e) => { patch((f) => (f.delivery.comments = e.target.value)); touch(); }} /></Field>
              </>
            )}

            {active === 'vessel' && (
              <Grid>
                <Field label="Model"><input className="field" value={modelName} disabled /></Field>
                <Field label="Model year"><input className="field" inputMode="numeric" value={form.vessel.modelYear} onChange={(e) => { patch((f) => (f.vessel.modelYear = e.target.value)); touch(); }} /></Field>
                <Field label="Flag"><input className="field" value={form.vessel.flag} onChange={(e) => { patch((f) => (f.vessel.flag = e.target.value)); touch(); }} /></Field>
                <Field label="Vessel name" note="Names the boat on the Order Sheet"><input className="field" value={form.vessel.vesselName} onChange={(e) => { patch((f) => (f.vessel.vesselName = e.target.value)); touch(); }} /></Field>
                <Field label="SAP number" note="? while it has none"><input className="field" value={form.vessel.sapNumber} onChange={(e) => { patch((f) => (f.vessel.sapNumber = e.target.value)); touch(); }} /></Field>
                <Field label="Hull number"><input className="field" value={form.vessel.hullNumber} onChange={(e) => { patch((f) => (f.vessel.hullNumber = e.target.value)); touch(); }} /></Field>
              </Grid>
            )}

            {active === 'standard' && (() => {
              const shown = stdCats.map((c) => ({ c, items: c.items.filter((it) => matches(it) && (!offOnly || form.lines[it.id]?.removedFromStandard)) })).filter((x) => x.items.length);
              return (
                <>
                  <div className="cfg-tools">
                    <SearchInput value={search} onChange={setSearch} placeholder="Search standard equipment" width={280} />
                    {removedStandard > 0 && <FilterToggle checked={offOnly} onChange={setOffOnly}>Taken off ({removedStandard})</FilterToggle>}
                  </div>
                  {!shown.length && <EmptyState title="Nothing matches" hint={q ? 'Try other words.' : 'Nothing is taken off this build.'} />}
                  {shown.map(({ c, items }) => {
                    const off = c.items.filter((it) => form.lines[it.id]?.removedFromStandard).length;
                    return (
                      <div key={c.id} className="cfg-cat">
                        <div className="cfg-cat-head">
                          <h3 className="cfg-cat-name">{c.name}</h3>
                          <span className="cfg-cat-meta">{off ? <Pill kind="warn">{off} taken off</Pill> : `${c.items.length} item${c.items.length === 1 ? '' : 's'}`}</span>
                        </div>
                        {items.map((it) => {
                          const removed = !!form.lines[it.id]?.removedFromStandard;
                          return (
                            <label key={it.id} className={`cfg-std-row${removed ? ' removed' : ''}`}>
                              <input type="checkbox" checked={!removed} onChange={(e) => setLine(it.id, { removedFromStandard: !e.target.checked })} />
                              <span className="cfg-std-name">{it.name}</span>
                              {removed && <Pill kind="warn">Taken off</Pill>}
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                </>
              );
            })()}

            {active === 'optional' && (() => {
              const shown = optCats.map((c) => ({ c, items: c.items.filter((it) => matches(it) && (!chosenOnly || form.lines[it.id]?.selected)) })).filter((x) => x.items.length);
              return (
                <>
                  <div className="cfg-tools">
                    <SearchInput value={search} onChange={setSearch} placeholder="Search options" width={280} />
                    {selectedOptional > 0 && <FilterToggle checked={chosenOnly} onChange={setChosenOnly}>Chosen ({selectedOptional})</FilterToggle>}
                  </div>
                  {!shown.length && <EmptyState title="Nothing matches" hint={q ? 'Try other words.' : 'No option is chosen yet.'} />}
                  {shown.map(({ c, items }) => {
                    const chosen = c.items.filter((it) => form.lines[it.id]?.selected);
                    const sub = chosen.reduce((n, it) => { const l = form.lines[it.id]!; return n + (l.customerPrice ?? it.customer_price ?? 0) * (l.quantity ?? 1); }, 0);
                    return (
                      <div key={c.id} className="cfg-cat">
                        <div className="cfg-cat-head">
                          <h3 className="cfg-cat-name">{c.name}</h3>
                          {c.discountEligible === false && <Pill title="Billed at full price: the dealer discount doesn’t apply">No dealer discount</Pill>}
                          <span className="cfg-cat-meta">{chosen.length ? <><b>{chosen.length} chosen</b>{sub ? ` · ${money(sub)}` : ''}</> : `${c.items.length} option${c.items.length === 1 ? '' : 's'}`}</span>
                        </div>
                        {items.map((it) => {
                          const l = form.lines[it.id] ?? {};
                          const sel = !!l.selected;
                          const pics = photosByRef.get(`item:${it.code}`) ?? [];
                          const qty = l.quantity ?? it.default_quantity ?? 1;
                          const price = l.customerPrice ?? it.customer_price ?? 0;
                          const repriced = l.customerPrice != null && it.customer_price != null && l.customerPrice !== it.customer_price;
                          return (
                            <div key={it.id} className={`cfg-opt-row${sel ? ' sel' : ''}`}>
                              {/* the diagram thumbnails are buttons beside the labels, not inside one, so a tap
                                  that misses a thumbnail can't tick or untick the option */}
                              <div className="cfg-opt-main">
                                <input id={`cfg-opt-${it.id}`} type="checkbox" checked={sel} onChange={(e) => setLine(it.id, { selected: e.target.checked, quantity: l.quantity ?? it.default_quantity ?? 1 })} />
                                <label htmlFor={`cfg-opt-${it.id}`} className="cfg-opt-name">{it.name}</label>
                                {pics.map((p) => (
                                  <button
                                    key={p.id}
                                    type="button"
                                    className="cfg-opt-thumb"
                                    title="Enlarge"
                                    aria-label={`Enlarge photo of ${it.name}`}
                                    onClick={(e) => openLightbox(`/api/photos/file?id=${p.id}`, e.currentTarget)}
                                  >
                                    <img src={`/api/photos/file?id=${p.id}`} alt="" loading="lazy" />
                                  </button>
                                ))}
                                <label htmlFor={`cfg-opt-${it.id}`} className="cfg-opt-price">{it.customer_price ? money(it.customer_price) : <span className="cfg-incl">Included</span>}</label>
                              </div>
                              {sel && (
                                <div className="cfg-opt-fields">
                                  <label className="cfg-opt-fld qty">
                                    <span className="cfg-opt-fld-lbl">Qty</span>
                                    <input className="field" type="number" min={1} inputMode="numeric" value={qty} onChange={(e) => setLine(it.id, { quantity: Number(e.target.value) })} />
                                  </label>
                                  <label className="cfg-opt-fld price">
                                    <span className="cfg-opt-fld-lbl">Price €</span>
                                    <input className="field" type="number" step="0.01" inputMode="decimal" value={l.customerPrice ?? it.customer_price ?? ''} onChange={(e) => setLine(it.id, { customerPrice: e.target.value === '' ? null : Number(e.target.value) })} />
                                  </label>
                                  <label className="cfg-opt-fld cmt">
                                    <span className="cfg-opt-fld-lbl">Comment</span>
                                    <input className="field" placeholder="Colour, finish, a note for the yard" value={l.color ?? ''} onChange={(e) => setLine(it.id, { color: e.target.value })} />
                                  </label>
                                  {it.has_drawing === 1 && (
                                    <label className="cfg-opt-fld drw">
                                      <span className="cfg-opt-fld-lbl">Drawing</span>
                                      <input className="field" placeholder="Reference" value={l.drawingReference ?? ''} onChange={(e) => setLine(it.id, { drawingReference: e.target.value })} />
                                    </label>
                                  )}
                                  {(qty > 1 || repriced) && (
                                    <span className="cfg-opt-line">
                                      {repriced && <span>List {money(it.customer_price!)}</span>}
                                      {qty > 1 && <span>Line <b>{money(price * qty)}</b></span>}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </>
              );
            })()}

            {active === 'custom' && (
              <>
                {form.customItems.map((ci, i) => (
                  <div key={i} className="cfg-custom">
                    <Field label="Request">
                      <input className="field" placeholder="What the customer asks for" value={ci.title} onChange={(e) => { patch((f) => { const c = f.customItems[i]; if (c) c.title = e.target.value; }); touch(); }} />
                    </Field>
                    <Field label="Qty">
                      <input className="field" type="number" min={1} inputMode="numeric" value={ci.quantity ?? 1} onChange={(e) => { patch((f) => { const c = f.customItems[i]; if (c) c.quantity = Number(e.target.value); }); touch(); }} />
                    </Field>
                    <button type="button" className="btn btn-sm btn-ghost cfg-custom-x" aria-label={`Remove request ${i + 1}`} title="Remove" onClick={() => { patch((f) => { f.customItems.splice(i, 1); }); touch(); }}><IconTrash size={15} /></button>
                    <Field label="Details" wide>
                      <textarea className="field" rows={2} value={ci.description ?? ''} onChange={(e) => { patch((f) => { const c = f.customItems[i]; if (c) c.description = e.target.value; }); touch(); }} />
                    </Field>
                  </div>
                ))}
                {!form.customItems.length && <p className="cfg-sec-empty">No custom requests on this order.</p>}
                <button type="button" className="btn btn-sm" onClick={() => { patch((f) => { f.customItems.push({ title: '', quantity: 1 }); }); touch(); }}><IconPlus size={14} /> Add a request</button>
              </>
            )}

            </fieldset>
            <footer className="cfg-sec-ft">
              {prev ? <button type="button" className="btn btn-sm btn-ghost" onClick={() => showSection(prev.key)}><IconChevronLeft size={15} /> {prev.label}</button> : <span />}
              {next
                ? <button type="button" className="btn btn-sm" onClick={() => showSection(next.key)}>{next.label} <IconChevronRight size={15} /></button>
                : readOnly ? <span /> : <button type="button" className="btn btn-sm btn-primary" disabled={primaryBusy || !model} onClick={primaryAct}>{committed ? primaryLabel : 'Review and confirm'}</button>}
            </footer>
          </section>
        </div>

        <aside className="cfg-summary" ref={summaryRef} aria-label="Order summary">
          <h2 className="cfg-sum-t">Order summary</h2>
          <p className="cfg-sum-boat">{[modelName, boatName].filter(Boolean).join(' · ') || 'New order'}</p>
          <div className="cfg-sum-group">
            <SumRow label="Options chosen" value={String(selectedOptional)} />
            <SumRow label="Standard taken off" value={String(removedStandard)} />
            <SumRow label="Custom requests" value={String(customCount)} />
          </div>
          <div className="cfg-sum-group">
            <SumRow label="Options subtotal" value={money(pricing.optional)} />
            <SumRow label={`Dealer discount ${DEALER_DISCOUNT_PCT}%`} value={`− ${money(pricing.discount)}`} />
            <SumRow label="Net ex-works" value={money(pricing.net)} />
            <SumRow label={`VAT ${VAT_PCT}%`} value={money(pricing.vat)} />
          </div>
          <div className="cfg-sum-total">
            <span>Total incl. VAT</span>
            <strong>{money(pricing.total)}</strong>
          </div>
          {!readOnly && <button className="btn btn-primary cfg-sum-btn" disabled={primaryBusy || !model} onClick={primaryAct}>{primaryLabel}</button>}
          <button className="btn cfg-sum-btn" onClick={exportAgreement} title="The customer’s boat sale agreement, to print or save as PDF">
            <IconFile size={14} /> Sales agreement
          </button>
          {printErr && <InlineError error={printErr} onDismiss={() => setPrintErr(null)} />}
          <p className="cfg-sum-note">
            {readOnly
              ? 'Locked: what you see is the order as it stands.'
              : committed
                ? manager
                  ? 'Saving makes a new revision for Assembly to accept, and files the order again on the Order Sheet.'
                  : 'Saving makes a new revision and locks the order again: Assembly accepts it, and it is filed again on the Order Sheet.'
                : 'Confirming sends the order to Assembly to accept, and files it: the boat’s Order Sheet row, its order file and its BOM.'}
          </p>
          {committed && can('order:confirm') && (
            <button type="button" className="btn btn-sm btn-ghost cfg-sum-refile" disabled={fileOrder.isPending || receipt?.busy} onClick={refile} title="Write the order on the Order Sheet and in Info / Files again, as it stands">
              File on the Order Sheet again
            </button>
          )}
          {editOrderMut.isError && <InlineError error={editOrderMut.error} onDismiss={() => editOrderMut.reset()} />}
        </aside>
      </div>

      {showConfirm && (
        <Modal
          title="Confirm order"
          onClose={() => setShowConfirm(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={confirmMissing.length > 0 || confirmOrder.isPending || !orderId} onClick={doConfirm}>
                {confirmOrder.isPending ? 'Confirming…' : 'Confirm and release'}
              </button>
            </>
          }
        >
          <div className="cfg-confirm">
            <SumRow label="Customer" value={form.buyer.name || '-'} />
            <SumRow label="Model" value={modelName || '-'} />
            <SumRow label="Vessel name" value={form.vessel.vesselName || '-'} />
            <SumRow label="SAP" value={form.vessel.sapNumber || '-'} />
            <SumRow label="Options chosen" value={String(selectedOptional)} />
            <SumRow label="Standard taken off" value={String(removedStandard)} />
            <SumRow label="Total incl. VAT" value={money(pricing.total)} strong />
          </div>
          {confirmMissing.length > 0 && (
            <div className="cfg-confirm-miss" role="alert">
              <IconAlertTriangle size={15} />
              <span>The order needs {confirmMissing.join(' and ')} before it can be confirmed.</span>
              <button type="button" className="btn btn-sm" onClick={() => { setShowConfirm(false); showSection('buyer'); }}>Buyer details</button>
            </div>
          )}
          {confirmOrder.isError && <InlineError error={confirmOrder.error} onDismiss={() => confirmOrder.reset()} />}
          <p className="cfg-confirm-note">
            I confirm that this order configuration is complete and may be released to Assembly, who review and accept it.
            Once confirmed it is locked: to change it later, Assembly opens it for you. Confirming also files it for
            production: the boat’s Order Sheet row, its order file and its BOM in Info / Files.
          </p>
        </Modal>
      )}

      {receipt && (
        <ReceiptModal
          receipt={receipt}
          canOpenFile={can('production:read')}
          onRetry={() => (receipt.filing && !receipt.files ? storeFiles(receipt.filing) : refile())}
          onClose={() => { setReceipt(null); navigate('/sales/orders'); }}
          onStay={() => setReceipt(null)}
        />
      )}

      {lightbox && (
        // a tap anywhere closes, as before; the controls stop their own clicks
        <div className="cfg-lb" role="dialog" aria-modal="true" aria-label="Photo preview" onClick={() => setLightbox(null)}>
          {lightboxSrc && <img className="cfg-lb-img" src={lightboxSrc} alt="Enlarged photo" />}
          <button type="button" className="cfg-lb-btn cfg-lb-x" aria-label="Close photo" autoFocus onClick={own(() => setLightbox(null))}>
            <IconClose size={22} />
          </button>
          {lightbox === 'gallery' && galleryCount > 1 && (
            <>
              <button type="button" className="cfg-lb-btn cfg-lb-nav prev" aria-label="Previous photo" onClick={own(() => stepGallery(-1))}><IconChevronLeft size={24} /></button>
              <button type="button" className="cfg-lb-btn cfg-lb-nav next" aria-label="Next photo" onClick={own(() => stepGallery(1))}><IconChevronRight size={24} /></button>
              <div className="cfg-lb-count">{gIdx + 1} / {galleryCount}</div>
            </>
          )}
        </div>
      )}

      {/* ≤900px: the running total within thumb reach (it jumps to the full summary) beside the summary's
          own primary action; wider screens keep the sticky summary column */}
      <ActionBar
        phoneOnly={false}
        className="cfg-bar"
        message={
          <button type="button" className="cfg-bar-total" onClick={scrollToSummary} aria-label={`${selectedOptional} options, total ${money(pricing.total)}. Show the order summary`}>
            {selectedOptional} option{selectedOptional === 1 ? '' : 's'} · <strong>{money(pricing.total)}</strong>
          </button>
        }
      >
        {!committed && !orderId && <button className="btn" disabled={busy || !model} onClick={() => save()}>Save draft</button>}
        {!readOnly && <button className="btn btn-primary" disabled={primaryBusy || !model} onClick={primaryAct}>{primaryLabel}</button>}
      </ActionBar>
    </>
  );
}

/** where a confirmed order stands with Assembly, and whether it can be changed: locked (and why), open for Sales
 *  (until when, why, what to change), or changeable by Assembly */
function LockLine({ order: o, readOnly, why, manager }: { order: OrderSummary; readOnly: boolean; why: string; manager: boolean }) {
  const df = useDateFormatter();
  const stage = orderStage(o);
  const toAccept = awaitsAcceptance(o);
  const accepted = o.acceptedRevision != null ? `Assembly accepted revision ${o.acceptedRevision}${o.acceptedRevision !== o.revisionNumber ? `; revision ${o.revisionNumber} waits to be accepted` : ''}.` : 'Assembly hasn’t accepted it yet.';
  if (readOnly) {
    return (
      <div className="cfg-lock" role="status">
        <IconLock size={16} />
        <div>
          <b>{stage === 'in_production' ? 'In production.' : 'Locked.'}</b>{' '}
          {/^You can’t/.test(why) ? why : stage === 'in_production' ? 'Only Assembly can change it now.' : 'Assembly can open it for you to change it.'} {accepted}
        </div>
      </div>
    );
  }
  if (o.salesOpen && !manager) {
    return (
      <div className="cfg-lock is-open" role="status">
        <IconUnlock size={16} />
        <div>
          <b>Open for you{o.salesOpen.until ? ` until ${df.dateTime(o.salesOpen.until)}` : ''}.</b>{' '}
          {o.salesOpen.reason ? `Assembly: “${o.salesOpen.reason}”. ` : ''}
          {o.salesOpen.instructions ? `${o.salesOpen.instructions}. ` : ''}
          Save your changes once, as a new revision: it then locks again and goes back to Assembly to accept.
        </div>
      </div>
    );
  }
  return (
    <div className="cfg-lock is-manager" role="status">
      <IconLock size={16} />
      <div>
        <b>{stage === 'in_production' ? 'In production.' : 'Locked for Sales.'}</b> You can change it as Assembly: saving makes a new revision{toAccept ? '' : ' for Assembly to accept'}. {accepted}
      </div>
    </div>
  );
}

/** what confirming or revising did: the order, its Order Sheet row and order file, and its workbooks */
function ReceiptModal({ receipt: r, canOpenFile, onRetry, onClose, onStay }: { receipt: Receipt; canOpenFile: boolean; onRetry: () => void; onClose: () => void; onStay: () => void }) {
  const failed = Boolean(r.filingError || r.filesError);
  const step = (state: 'ok' | 'warn' | 'err' | 'busy' | 'todo', title: ReactNode, detail?: ReactNode) => (
    <li className={`cfg-rc-step ${state}`}>
      <span className="cfg-rc-ic" aria-hidden="true">
        {state === 'busy' ? <LogoSpin size={16} decorative /> : state === 'ok' ? <IconCheck size={14} /> : state === 'todo' ? null : <IconAlertTriangle size={14} />}
      </span>
      <span className="cfg-rc-txt"><b>{title}</b>{detail && <span>{detail}</span>}</span>
    </li>
  );
  const f = r.filing;
  return (
    <Modal
      title={r.kind === 'confirmed' ? 'Order confirmed' : r.kind === 'revised' ? 'Revision saved' : 'Filed again'}
      onClose={onClose}
      footer={
        <>
          {failed && !r.busy && <button className="btn" onClick={onRetry}>Try again</button>}
          {canOpenFile && f && <Link className="btn" to={`/order-file/${encodeURIComponent(f.sap)}?season=${encodeURIComponent(f.season)}`}>Order file</Link>}
          <button className="btn btn-ghost" onClick={onStay}>Stay here</button>
          <button className="btn btn-primary" onClick={onClose}>Sales orders</button>
        </>
      }
    >
      <ol className="cfg-rc" aria-live="polite">
        {r.revision > 0 && step('ok', r.kind === 'confirmed' ? `Confirmed as revision ${r.revision}` : `Revision ${r.revision} saved`, 'Assembly has been told.')}
        {r.filingError ? step('err', 'Not filed on the Order Sheet', r.filingError)
          : f ? step('ok', `Order Sheet row ${f.sap.startsWith('name:') ? `for ${f.name}` : `for SAP ${f.sap}`}`, `Order file revision ${f.revision} · ${f.on} row${f.on === 1 ? '' : 's'} ticked or filled in`)
          : step('busy', 'Filing on the Order Sheet…')}
        {f && (r.files
          ? step(r.files.bomKey ? 'ok' : 'warn', r.files.bomKey ? 'Order and BOM in Info / Files' : 'Order in Info / Files, without a BOM', <>{r.files.folder}{r.files.bomWhy ? ` · ${r.files.bomWhy}` : ''}</>)
          : r.filesError ? step('err', 'The workbooks aren’t in Info / Files', r.filesError)
          : step('busy', 'Making the order and BOM workbooks…'))}
      </ol>
    </Modal>
  );
}

const Grid = ({ children }: { children: ReactNode }) => <div className="cfg-grid">{children}</div>;
// wrapping <label> names the single control inside it (no htmlFor/id plumbing needed)
const Field = ({ label, note, warn, wide, children }: { label: string; note?: string; warn?: boolean; wide?: boolean; children: ReactNode }) => (
  <label className={`cfg-field${wide ? ' wide' : ''}`}>
    <span className="field-label">{label}{note && <span className={`cfg-field-note${warn ? ' warn' : ''}`}>{note}</span>}</span>
    {children}
  </label>
);
const SumRow = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div className={`cfg-sum-row ${strong ? 'strong' : ''}`}><span>{label}</span><span>{value}</span></div>
);
